import type { UIMessage } from 'ai'

import type { AssistantContextRef } from '~/data/api/client'
import { type FocusedContextRef, toFocusedContextRef } from '~/features/assistant/contextRef'

/** A message as the composer hands it over: its text and the entities tagged with `@`. */
export type ComposedMessage = {
  text: string
  contextRefs: FocusedContextRef[]
}

export function contextRefKey(ref: Pick<FocusedContextRef, 'type' | 'id'>): string {
  return `${ref.type}:${ref.id}`
}

/** `refs` plus `ref`, unless an entity of the same type and id is already among them. */
export function withContextRef(refs: FocusedContextRef[], ref: FocusedContextRef): FocusedContextRef[] {
  const key = contextRefKey(ref)
  return refs.some((item) => contextRefKey(item) === key) ? refs : [...refs, ref]
}

/**
 * The entities a user message was sent with. They travel on the message as `metadata.contextRefs`, which
 * assistant-api stores with the transcript, so a reloaded conversation shows them too.
 */
export function readContextRefs(message: UIMessage): FocusedContextRef[] {
  const { metadata } = message
  if (typeof metadata !== 'object' || metadata === null || !('contextRefs' in metadata)) return []
  const { contextRefs } = metadata
  if (!Array.isArray(contextRefs)) return []
  return contextRefs.flatMap((ref: unknown) => {
    if (typeof ref !== 'object' || ref === null) return []
    const focused = toFocusedContextRef(ref as AssistantContextRef)
    return focused ? [focused] : []
  })
}
